import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_POSTS_BY_CATEGORY } from '../../graphql/queries';
import Sidebar from '../blog-components/sidebar';

const CategoryPosts = ({ categorySlug }) => {
    const publicUrl = process.env.PUBLIC_URL + '/';
    const [loadingMore, setLoadingMore] = useState(false);

    // Fetch posts for this category with pagination
    const { data, loading, error, fetchMore } = useQuery(GET_POSTS_BY_CATEGORY, {
        variables: {
            categorySlug: categorySlug,
            first: 9,
            after: null
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network'
    });

    const posts = data?.posts?.nodes || [];
    const pageInfo = data?.posts?.pageInfo;
    const hasNextPage = pageInfo?.hasNextPage;
    const endCursor = pageInfo?.endCursor;

    // Helper function to format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    // Helper function to get excerpt
    const getExcerpt = (excerpt, maxLength = 150) => {
        if (!excerpt) return '';
        const text = excerpt.replace(/<[^>]*>/g, '');
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Helper function to get featured image
    const getFeaturedImage = (post, index) => {
        return post?.featuredImage?.node?.sourceUrl || publicUrl + `assets/img/post/${index + 1}.png`;
    };

    // Helper function to get first category
    const getFirstCategory = (post) => {
        return post?.categories?.nodes?.[0] || { name: 'Uncategorized', slug: 'uncategorized' };
    };

    // Load more posts
    const handleLoadMore = async () => {
        if (!hasNextPage || loadingMore) return;

        setLoadingMore(true);
        try {
            await fetchMore({
                variables: {
                    after: endCursor
                }
            });
        } catch (err) {
            console.error('Error loading more posts:', err);
        }
        setLoadingMore(false);
    };

    if (loading && !loadingMore) {
        return (
            <div className="blog-page-area pd-top-80 pd-bottom-80 go-top">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 text-center">
                            <p>Loading posts...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="blog-page-area pd-top-80 pd-bottom-80 go-top">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 text-center">
                            <h3>Error loading category</h3>
                            <p>Unable to load posts for this category.</p>
                            <Link to="/" className="btn btn-blue mt-3">Go to Homepage</Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (posts.length === 0) {
        return (
            <div className="blog-page-area pd-top-80 pd-bottom-80 go-top">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 text-center">
                            <h3>No posts found</h3>
                            <p>This category doesn't have any posts yet.</p>
                            <Link to="/" className="btn btn-blue mt-3">Go to Homepage</Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-page-area pd-top-80 pd-bottom-80 go-top">
            <div className="container">
                <div className="row">
                    <div className="col-lg-9">
                        {/* Category Info */}
                        <div className="section-title mb-4">
                            <h3 className="title">
                                {getFirstCategory(posts[0]).name}
                            </h3>
                            <p>Showing {posts.length} post{posts.length !== 1 ? 's' : ''}</p>
                        </div>

                        {/* Posts Grid */}
                        <div className="row">
                            {posts.map((post, index) => {
                                const category = getFirstCategory(post);
                                return (
                                    <div className="col-lg-6 col-md-6" key={post.id}>
                                        <div className="single-blog-inner mb-4">
                                            <div className="thumb">
                                                <img
                                                    src={getFeaturedImage(post, index)}
                                                    alt={post?.featuredImage?.node?.altText || post.title}
                                                />
                                            </div>
                                            <div className="details">
                                                <div className="post-meta-single mb-2">
                                                    <ul>
                                                        <li>
                                                            <Link className="tag-base tag-purple" to={`/category/${category.slug}`} style={{borderRadius: '20px',  fontSize: '12px'}}>
                                                                {category.name}
                                                            </Link>
                                                        </li>
                                                        <li>
                                                            <i className="fa fa-clock-o" />
                                                            {formatDate(post.date)}
                                                        </li>
                                                    </ul>
                                                </div>
                                                <h5 className="title">
                                                    <Link
                                                        to={`/blog/${post.slug}`}
                                                        dangerouslySetInnerHTML={{ __html: post.title }}
                                                    />
                                                </h5>
                                                <p>{getExcerpt(post.excerpt)}</p>
                                                <Link className="read-more-text" to={`/blog/${post.slug}`}>
                                                    Read More <i className="fa fa-angle-right" />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Load More Button */}
                        {hasNextPage && (
                            <div className="row">
                                <div className="col-lg-12 text-center">
                                    <button
                                        className="btn btn-blue"
                                        onClick={handleLoadMore}
                                        disabled={loadingMore}
                                    >
                                        {loadingMore ? 'Loading...' : 'Load More Posts'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="col-lg-3">
                        <Sidebar />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryPosts;
